/**
 * La scheda Conti: un mese, letto dall'alto verso il basso.
 *
 * Il mese si sceglie una volta in cima e tutto quello che sta sotto lo segue:
 * quattro numeri per capire al volo, il rendiconto riga per riga accanto alla
 * ciambella che dice dove vanno i soldi, sei mesi di barre per vedere la
 * forma, e in fondo le impostazioni che cambiano i numeri.
 */

import { useCallback, useEffect, useState } from "react";
import {
  BarChart3,
  Euro,
  FilePlus2,
  Landmark,
  PiggyBank,
  Receipt,
  SlidersHorizontal,
  Wallet,
} from "lucide-react";
import {
  formatEur,
  formatMese,
  meseDi,
  spostaMese,
  todayInRome,
  type AndamentoMese,
  type ConfrontoBaseline,
  type Mese,
  type ProfittoImmobile,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { Card, Pill } from "../ui/primitives.tsx";
import { useStore } from "../lib/store.tsx";
import {
  GrigliaKpi,
  IntestazioneRiquadro,
  Kpi,
  NavigatoreMese,
  Sezione,
  type DeltaKpi,
} from "../ui/Sezione.tsx";
import { BarreImpilate } from "../ui/Grafici.tsx";
import { Rendiconto } from "./ContoCard.tsx";
import { DoveVanno } from "./TrattenuteCard.tsx";
import { RapportoCard } from "./RapportoCard.tsx";
import { RicorrentiCard } from "./RicorrentiCard.tsx";
import { DichiaraConti } from "./DichiaraConti.tsx";

/** Quanti mesi indietro mostrare nel grafico. */
const MESI_DI_STORIA = 6;

const MESI_BREVI = [
  "gen",
  "feb",
  "mar",
  "apr",
  "mag",
  "giu",
  "lug",
  "ago",
  "set",
  "ott",
  "nov",
  "dic",
];
function meseBreve(mese: Mese): string {
  return MESI_BREVI[Number(mese.slice(5, 7)) - 1] ?? mese;
}

export function ContiTab({
  listingId,
  onCambiato,
}: {
  listingId: string;
  /** Rapporto o costi ricorrenti cambiati: i numeri vanno ricalcolati. */
  onCambiato: () => void;
}) {
  const [mese, setMese] = useState<Mese>(() => meseDi(todayInRome()));
  const [profitto, setProfitto] = useState<ProfittoImmobile | null>(null);
  const [confronto, setConfronto] = useState<ConfrontoBaseline | null>(null);
  const [andamento, setAndamento] = useState<AndamentoMese[] | null>(null);
  const [errore, setErrore] = useState(false);
  const [dichiara, setDichiara] = useState(false);
  const store = useStore();

  const ricarica = useCallback(async () => {
    const periodo = { da: spostaMese(mese, -(MESI_DI_STORIA - 1)), a: mese };
    const [conto, paragone, storia] = await Promise.all([
      api.profitto(listingId, mese).catch(() => null),
      // Il resto non deve poter far sparire il conto.
      api.confronto(listingId, mese).catch(() => null),
      api.andamento(listingId, periodo).catch(() => null),
    ]);
    if (!conto) setErrore(true);
    setProfitto(conto?.profitto ?? null);
    setConfronto(paragone?.confronto ?? null);
    setAndamento(storia?.andamento ?? null);
  }, [listingId, mese]);

  useEffect(() => {
    setProfitto(null);
    setConfronto(null);
    setAndamento(null);
    setErrore(false);
    void ricarica();
  }, [ricarica]);

  /**
   * Su ogni mese, lo stesso riquadro: i numeri si possono inserire o correggere
   * a mano. Uguale per tutti apposta (12/10/2026): mostrato solo su alcuni
   * mesi sembrava una funzione che c'è a volte sì e a volte no. Su un mese
   * non ancora chiuso il modulo avvisa di cosa vuol dire.
   */
  const dichiaratoIl = profitto?.dichiaratoIl ?? null;
  const conConto = profitto != null;

  const r = profitto?.rendiconto;
  const inTabella =
    profitto?.righe
      .filter((x) => x.categoria === "commissioni_ota")
      .reduce((s, x) => s + x.importoCents, 0) ?? 0;
  // Trattenuto dai canali: quello già tolto da Airbnb più quello che Booking
  // fattura a parte (commissione, incasso e IVA).
  const trattenuto = (r?.commissioniTrattenuteCents ?? 0) + inTabella;
  const soggiorno =
    profitto?.righe
      .filter((x) => x.categoria === "tassa_soggiorno")
      .reduce((s, x) => s + x.importoCents, 0) ?? 0;
  const altriCosti = (profitto?.costiOperativiCents ?? 0) - inTabella;
  const ricavi = profitto?.ricaviLordiCents ?? 0;
  const inPerdita = (profitto?.utileGestoreCents ?? 0) < 0;
  /**
   * Il mese sta ancora vendendosi?
   *
   * Lo stato lo dice il ledger (`statoMese`), con il giorno di chiusura vero:
   * un mese finito ieri non è chiuso, perché gli accrediti del canale devono
   * ancora arrivare. Su un mese che non è finito la domanda non è «quanto ho
   * guadagnato» ma «quanto c'è già in cassa e quante notti restano», e
   * finora quella risposta viveva solo in chat.
   */
  // Un mese con i numeri inseriti dall'host non sta vendendo: l'ha chiuso lui.
  const staVendendo =
    !profitto?.dichiaratoIl &&
    (profitto?.statoMese === "futuro" || profitto?.statoMese === "in_corso");

  const deltaResta: DeltaKpi | null =
    confronto &&
    confronto.base !== "insufficiente" &&
    confronto.differenzaPercento != null
      ? {
          testo: `${confronto.differenzaPercento >= 0 ? "+" : "−"}${String(
            Math.abs(confronto.differenzaPercento),
          ).replace(".", ",")}%`,
          positivo: confronto.differenzaPercento >= 0,
        }
      : null;

  // Il mese prima, per dire «rispetto ad agosto».
  const precedente =
    andamento?.find((m) => m.mese === spostaMese(mese, -1)) ?? null;
  const deltaSu = (
    attuale: number,
    prima: number | null | undefined,
    costo = false,
  ): DeltaKpi | null => {
    if (prima == null || prima === 0) return null;
    const p = Math.round(((attuale - prima) / Math.abs(prima)) * 100);
    if (!Number.isFinite(p)) return null;
    return {
      testo: `${p >= 0 ? "+" : "−"}${Math.abs(p)}%`,
      positivo: costo ? p <= 0 : p >= 0,
    };
  };

  return (
    <div className="flex flex-col gap-7">
      <Sezione
        icon={<Wallet size={15} />}
        titolo="Quanto rende"
        sottotitolo="Ricavi, trattenute dei canali, costi: e quanto ti resta."
        azione={<NavigatoreMese mese={mese} onChange={setMese} />}
      >
        {errore ? (
          <p className="rounded-card bg-surface-muted px-4 py-3 text-[13px] text-ink-soft">
            Non sono riuscito a calcolare questo mese.
          </p>
        ) : (
          <GrigliaKpi>
            <Kpi
              icon={<Euro size={17} />}
              etichetta="Incassato"
              valore={profitto ? formatEur(ricavi) : ""}
              delta={deltaSu(ricavi, precedente?.ricaviCents)}
              barra={
                profitto
                  ? {
                      quota:
                        profitto.nottiVendute /
                        Math.max(1, profitto.nottiDisponibili),
                      estremi: [
                        `${profitto.nottiVendute} notti`,
                        `${profitto.nottiDisponibili}`,
                      ],
                    }
                  : null
              }
              dettaglio={
                staVendendo && profitto
                  ? `già prenotato · restano ${profitto.nottiLibere} ${
                      profitto.nottiLibere === 1 ? "notte" : "notti"
                    } da vendere`
                  : dichiaratoIl
                    ? "inserito da te"
                    : r && r.commissioniTrattenuteCents > 0
                      ? `il numero di Guesty · venduto ${formatEur(r.vendutoLordoCents)}`
                      : "il numero di Guesty: somma dei payout del mese"
              }
              caricamento={!profitto}
            />
            <Kpi
              icon={<Landmark size={17} />}
              etichetta="Trattenuto dai canali"
              valore={profitto ? formatEur(trattenuto) : ""}
              delta={deltaSu(trattenuto, precedente?.trattenuteCents, true)}
              barra={
                profitto
                  ? { quota: ricavi > 0 ? trattenuto / ricavi : 0 }
                  : null
              }
              dettaglio={
                dichiaratoIl
                  ? "commissioni pagate a parte, inserite da te"
                  : r && r.commissioniTrattenuteCents > 0
                    ? `Airbnb ${formatEur(r.commissioniTrattenuteCents)} già tolti · Booking ${formatEur(inTabella)} a parte`
                    : "commissioni, incasso e IVA"
              }
              caricamento={!profitto}
            />
            <Kpi
              icon={<Receipt size={17} />}
              etichetta="Costi della casa"
              valore={profitto ? formatEur(Math.max(0, altriCosti)) : ""}
              delta={deltaSu(altriCosti, precedente?.costiCents, true)}
              barra={
                profitto
                  ? { quota: ricavi > 0 ? altriCosti / ricavi : 0 }
                  : null
              }
              dettaglio={
                soggiorno > 0
                  ? `di cui ${formatEur(soggiorno)} di imposta di soggiorno da versare`
                  : profitto && profitto.stimatoCents > 0
                    ? `pulizie, imposte e spese · ${formatEur(profitto.stimatoCents)} stimati`
                    : "pulizie, imposte e spese"
              }
              caricamento={!profitto}
            />
            <Kpi
              icon={<PiggyBank size={17} />}
              etichetta="Ti resta"
              valore={profitto ? formatEur(profitto.utileGestoreCents) : ""}
              delta={
                deltaResta ??
                deltaSu(
                  profitto?.utileGestoreCents ?? 0,
                  precedente?.utileCents,
                )
              }
              barra={
                profitto
                  ? {
                      quota:
                        ricavi > 0
                          ? Math.max(0, profitto.utileGestoreCents) / ricavi
                          : 0,
                    }
                  : null
              }
              dettaglio={
                deltaResta
                  ? "dopo canali, Comune e costi · variazione sull'anno scorso"
                  : precedente
                    ? "dopo canali, Comune e costi · variazione sul mese prima"
                    : "dopo canali, Comune e costi"
              }
              tone={inPerdita ? "danger" : "accent"}
              caricamento={!profitto}
            />
          </GrigliaKpi>
        )}
      </Sezione>

      {/* ── I numeri a mano, su ogni mese ─────────────────────────────── */}
      {conConto ? (
        <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-field bg-surface-muted text-ink-soft">
              <FilePlus2 size={17} />
            </span>
            <p className="min-w-0 text-[13.5px] font-semibold text-ink">
              {dichiaratoIl
                ? `Numeri inseriti da te il ${new Date(dichiaratoIl).toLocaleDateString("it-IT")}.`
                : "I numeri non tornano o non avevi ancora Guesty collegato?"}
            </p>
          </div>
          <Pill
            variant="secondary"
            size="sm"
            onClick={() => setDichiara(true)}
            className="shrink-0 self-start sm:self-auto"
          >
            {dichiaratoIl ? "Correggi" : "Inserisci i numeri"}
          </Pill>
        </Card>
      ) : null}

      {/* ── Il rendiconto e dove vanno i soldi, fianco a fianco ─────────── */}
      {profitto ? (
        <div className="grid items-stretch gap-5 lg:grid-cols-5">
          <div className="min-w-0 lg:col-span-3">
            <Rendiconto
              profitto={profitto}
              confronto={confronto}
              listingId={listingId}
              onConfermato={ricarica}
              {...(dichiaratoIl ? { onCorreggi: () => setDichiara(true) } : {})}
            />
          </div>
          <div className="min-w-0 lg:col-span-2">
            <DoveVanno profitto={profitto} />
          </div>
        </div>
      ) : null}

      {/* ── La forma degli ultimi mesi ───────────────────────────────────── */}
      {andamento && andamento.length > 1 ? (
        <Card className="p-5">
          <IntestazioneRiquadro
            icon={<BarChart3 size={15} />}
            titolo={`Gli ultimi ${andamento.length} mesi`}
            nota="venduto, e come si divide"
          />
          <BarreImpilate
            formatta={(v) => formatEur(Math.round(v))}
            colonne={andamento.map((m) => ({
              etichetta: meseBreve(m.mese),
              attiva: m.mese === mese,
              onClick: () => setMese(m.mese),
              /**
               * Il totale della barra è il **venduto**, non l'incassato.
               *
               * Quello che Airbnb trattiene prima di versare dentro
               * l'incassato non c'è mai passato: usarlo come totale faceva
               * una barra più corta delle sue stesse fette. Le tre fette
               * sommano a `vendutoCents` — e dove non ci arrivano (mandato o
               * affitto: il trasferimento al proprietario è una quarta fetta
               * che qui non si disegna) la differenza resta visibile invece
               * di sparire nella scala.
               */
              totale: m.vendutoCents,
              segmenti: [
                { nome: "Ti resta", valore: m.utileCents, classe: "bg-ok" },
                {
                  nome: "Trattenute dei canali",
                  valore: m.trattenuteCents,
                  classe: "bg-accent",
                },
                {
                  nome: "Costi della casa",
                  valore: m.costiCents,
                  classe: "bg-line-strong",
                },
              ],
            }))}
          />
        </Card>
      ) : null}

      <Sezione
        icon={<SlidersHorizontal size={15} />}
        titolo="Come dividi e cosa paghi ogni mese"
        sottotitolo="Cambiando qualcosa qui, il conto sopra si ricalcola."
      >
        <div className="grid items-stretch gap-5 lg:grid-cols-2">
          <RapportoCard listingId={listingId} onSalvato={onCambiato} />
          <RicorrentiCard listingId={listingId} onCambiato={onCambiato} />
        </div>
      </Sezione>

      <DichiaraConti
        aperta={dichiara}
        onChiudi={() => setDichiara(false)}
        listingId={listingId}
        nickname={profitto?.nickname ?? ""}
        mese={mese}
        profitto={profitto}
        onSalvato={(nuovo) => {
          setDichiara(false);
          setProfitto(nuovo);
          store.notify(`Conti di ${formatMese(mese)} salvati.`);
          // Il confronto e il grafico dipendono da questo mese: si rileggono.
          void ricarica();
        }}
      />
    </div>
  );
}
